import React, { useEffect } from 'react';
import { FormikProvider, Form, useFormik, FormikErrors } from 'formik';
import {
  BubbleAlert,
  BubbleButton,
  BubbleFormGroup,
  BubbleSelect,
  BubbleTypeaheadSimple,
} from '../BubbleUI';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { Property } from '@go-shared/types/property';
import { canModifyBrandSettings, canModifyMultiPropertyItem } from '@go-shared/lib/permissions';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import {
  getMultiPropertyKind,
  MultiPropertyItem,
  MultiPropertyItemBase,
  MultiPropertyKind,
} from '@go-shared/lib/libraries';
import { usePropertyInfo } from '@go-client/context/PropertyInfoContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface PropertyAccessFormikForm {
  status: MultiPropertyKind['status'];
  property: string;
  propertyList: string[];
}

export type WithAccessMode<T extends { type: string }> = Omit<T, 'type'> & {
  type: T['type'] | 'access';
};

function GENERATE_INITIAL_VALUES<T extends MultiPropertyItemBase, U extends MultiPropertyItem<T>>(
  edit: U,
  defaultProperty: Property
): PropertyAccessFormikForm {
  const kind = getMultiPropertyKind(edit);

  return {
    status: kind.status,
    property: edit.source || defaultProperty._id,
    propertyList: edit.properties.map(p => p.property),
  };
}

interface PropertyAccessFormikPropsBase<
  T extends MultiPropertyItemBase,
  U extends MultiPropertyItem<T>
> {
  edit: U;
  makePropertyAccessItem: (item: string) => T;
  updateFn: (edit: U, action?: 'promote' | 'demote') => Promise<unknown>;
  subnavClassName?: string;
  actionsClassName?: string;
  actionsAppearance?: 'drawer-float';
  onSubmit?: () => unknown;
}

type PropertyAccessFormikProps<
  T extends MultiPropertyItemBase,
  U extends MultiPropertyItem<T>
> = PropertyAccessFormikPropsBase<T, U>;

function PropertyAccessFormik<T extends MultiPropertyItemBase, U extends MultiPropertyItem<T>>(
  props: PropertyAccessFormikProps<T, U>
) {
  const { user } = useUserInfo();
  const serverInfo = useServerInfo();
  const { activeProperty: property } = useActiveProperty();
  const { allProperties, allPropertiesDict } = usePropertyInfo();

  const formik = useFormik({
    initialValues: GENERATE_INITIAL_VALUES(props.edit, property),
    validate(values) {
      const errors: FormikErrors<PropertyAccessFormikForm> = {};

      switch (values.status) {
        case 'property':
          if (!values.property) {
            errors.property = 'Required';
          }
          break;
        case 'multi':
          if (values.propertyList.length < 1) {
            errors.propertyList = 'Required';
          }
          break;
      }

      return errors;
    },
    onSubmit: onFormSubmit,
  });

  useEffect(() => {
    formik.resetForm({ values: GENERATE_INITIAL_VALUES(props.edit, property) });
  }, [props.edit]);

  const { values, errors, touched, setFieldValue, handleSubmit, handleBlur, dirty } = formik;

  const actionClasses = [];
  if (props.actionsAppearance === 'drawer-float')
    actionClasses.push('sticky mt-3 bottom-0 z-20 -mx-8 p-8 pt-4 form-blur');
  if (props.actionsClassName) actionClasses.push(props.actionsClassName);

  const subnavClasses = ['flex items-center mb-6'];
  if (props.subnavClassName) subnavClasses.push(props.subnavClassName);

  async function onFormSubmit(values: PropertyAccessFormikForm) {
    let action: 'promote' | 'demote' | undefined = undefined;

    const newTmpl: U = {
      ...props.edit,
    };

    switch (values.status) {
      case 'brand':
        action = 'promote';
        delete newTmpl.source;
        break;
      case 'property':
        action = 'demote';
        newTmpl.source = values.property;
        newTmpl.properties = [props.makePropertyAccessItem(values.property)];
        break;
      case 'multi':
        newTmpl.source = values.propertyList[0];
        newTmpl.properties = values.propertyList.map(props.makePropertyAccessItem);
        break;
    }

    await props.updateFn(newTmpl, action);

    if (props.onSubmit) {
      props.onSubmit();
    }
  }

  const properties = allProperties.filter(p => p.active).map(p => p._id);
  const canModifyBrand = canModifyBrandSettings(user, serverInfo);
  const canModifyMulti = canModifyMultiPropertyItem(user, serverInfo, property._id, props.edit);
  const locked = !canModifyBrand;
  const disabled = locked || formik.isSubmitting || !formik.isValid || !dirty;

  return (
    <FormikProvider value={formik}>
      <div className={subnavClasses.join(' ')}>
        <BubbleButton
          block
          appearance="squared"
          variant={values.status === 'property' ? 'navy' : 'smoke'}
          onClick={() => setFieldValue('status', 'property')}
          disabled={locked}
        >
          <FontAwesomeIcon
            icon={values.status === 'property' ? 'circle-dot' : 'circle'}
            fixedWidth
            className="mr-1"
          />
          Property
        </BubbleButton>
        <BubbleButton
          block
          appearance="squared"
          variant={values.status === 'multi' ? 'navy' : 'smoke'}
          onClick={() => setFieldValue('status', 'multi')}
          disabled={locked || !canModifyMulti}
        >
          <FontAwesomeIcon
            icon={values.status === 'multi' ? 'circle-dot' : 'circle'}
            fixedWidth
            className="mr-1"
          />
          Multi
        </BubbleButton>
        <BubbleButton
          block
          appearance="squared"
          variant={values.status === 'brand' ? 'navy' : 'smoke'}
          onClick={() => setFieldValue('status', 'brand')}
          disabled={locked}
        >
          <FontAwesomeIcon
            icon={values.status === 'brand' ? 'circle-dot' : 'circle'}
            fixedWidth
            className="mr-1"
          />
          Brand
        </BubbleButton>
      </div>
      <Form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4">
        {locked && (
          <BubbleAlert className="rounded-0-i" variant="ocean">
            <span className="font-semibold">
              This content access management cannot be modified.
            </span>{' '}
            You may be in a property level instance or you lack brand permissions.
          </BubbleAlert>
        )}

        {values.status === 'property' ? (
          <>
            <BubbleFormGroup
              label="Property"
              error={errors.property && touched.property && errors.property}
            >
              <BubbleSelect
                block
                name="property"
                allowUndefined={false}
                value={values.property}
                options={properties}
                labelExtractor={p => allPropertiesDict[p].name}
                onValue={val => setFieldValue('property', val)}
                onBlur={handleBlur}
                disabled={!locked}
              />
            </BubbleFormGroup>
          </>
        ) : values.status === 'multi' ? (
          <>
            <BubbleFormGroup
              label="Properties"
              className="-mt-2"
              error={errors.propertyList && touched.propertyList && errors.propertyList}
              labelFn={p => (
                <div className="flex items-end">
                  <div className="mr-auto">
                    {p.label}
                    <span className="text-crimson">&nbsp;*</span>
                  </div>
                  {!locked && (
                    <BubbleButton
                      variant="ocean"
                      inverse
                      className="p-1-i"
                      onClick={() => setFieldValue('propertyList', properties)}
                    >
                      Add All Properties
                    </BubbleButton>
                  )}
                </div>
              )}
            >
              <BubbleTypeaheadSimple
                block
                labelExtractor={el => allPropertiesDict[el].name}
                placeholder="Add additional property..."
                menuClassName="max-h-80 overflow-y-auto"
                value={values.propertyList}
                options={properties}
                onValue={vals => setFieldValue('propertyList', vals)}
                onBlur={handleBlur}
                readOnly={locked || !canModifyMulti}
              />
            </BubbleFormGroup>
          </>
        ) : (
          <>
            <BubbleAlert variant="ocean">
              This content will be visible to all properties.
            </BubbleAlert>
          </>
        )}
        <div className={actionClasses.join(' ')}>
          <div className="flex items-center">
            <div></div>
            <div className="ml-auto" />
            <BubbleButton
              variant="navy"
              type="reset"
              inverse
              icon="rotate-left"
              disabled={disabled}
            >
              Reset
            </BubbleButton>
            <BubbleButton variant="navy" type="submit" icon="floppy-disk" disabled={disabled}>
              Save Changes
            </BubbleButton>
          </div>
        </div>
      </Form>
    </FormikProvider>
  );
}

export default PropertyAccessFormik;
